import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ArrowDown, ArrowUp, Crown, Sparkles, Trophy, Volume2, WifiOff } from 'lucide-react';
import { FullscreenButton } from '../app/cast';
import type { GameId, ScoreLine } from '@gmp/shared-types/browser';
import { Avatar, Countdown, Logo, usePartyUi } from '@gmp/shared-ui';
import { ART } from '../app/art';
import { gameInfo } from '../app/catalog';
import { PHASE_LABEL, phaseCaption } from '../app/games';
import { sound } from '../app/prefs';
import { QR } from '../app/QR';
import { TvMusicControls } from '../app/tvMusic';
import { ICON } from './GameCard';

/** Viewport height, live (the frame sizes its chrome from it so 720p and 1080p both read well). */
export function useViewportHeight(): number {
  const [h, setH] = useState(() => window.innerHeight);
  useEffect(() => {
    const on = () => setH(window.innerHeight);
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);
  return h;
}

const clamp = (lo: number, v: number, hi: number) => Math.max(lo, Math.min(hi, v));

/** The game's theme colours as frame tokens (the game's own --accent stays untouched for its content). */
export function tvTheme(gameId: GameId): CSSProperties {
  const t = gameInfo(gameId).theme;
  return { '--tv': t.primary, '--tv-2': t.secondary, '--tv-bg': t.background } as CSSProperties;
}

/** Game icon art (or its lucide badge while the art pack has no icon yet). */
export function GameIcon({ gameId, size }: { gameId: GameId; size: number }) {
  const art = ART[gameId];
  if (art?.icon) return <img src={art.icon} alt="" aria-hidden width={size} height={size} draggable={false} className="shrink-0 object-contain drop-shadow-[0_6px_10px_rgba(0,0,0,0.5)] select-none" style={{ width: size, height: size }} />;
  const I = ICON[gameId] ?? Sparkles;
  const g = gameInfo(gameId);
  return (
    <span className="grid place-items-center rounded-2xl shrink-0" style={{ width: size, height: size, background: g.theme.primary, color: g.theme.background }} aria-hidden>
      <I size={size * 0.55} />
    </span>
  );
}

/**
 * Header strip: the game's cover as a soft band behind its icon and name, the round and phase with a
 * one-line caption of what's happening, the timer, and the party code + join QR so people can drop in.
 */
export function TvHeader({ gameId, kicker, caption, endsAt, startedAt, quietTimer, showJoin, joinUrl, code }: {
  gameId: GameId; kicker: string; caption?: string; endsAt?: number | null; startedAt?: number; quietTimer?: boolean; showJoin: boolean; joinUrl: string; code: string;
}) {
  const vh = useViewportHeight();
  const g = gameInfo(gameId);
  const art = ART[gameId];
  const H = Math.round(clamp(76, vh * 0.105, 124));
  const qr = Math.round(H * 0.78);
  return (
    <header className="relative shrink-0 overflow-hidden border-b-2" style={{ height: H, borderColor: 'color-mix(in oklab, var(--tv) 55%, transparent)', background: 'color-mix(in oklab, var(--tv-bg) 80%, black)' }} data-testid="tv-header">
      {art && (
        <img
          src={art.cover} alt="" aria-hidden draggable={false}
          className="absolute inset-0 w-full h-full object-cover opacity-45 pointer-events-none select-none"
          style={{ objectPosition: '50% 35%', maskImage: 'linear-gradient(to right, black 0%, rgba(0,0,0,0.75) 35%, rgba(0,0,0,0.25) 70%, transparent 100%)', WebkitMaskImage: 'linear-gradient(to right, black 0%, rgba(0,0,0,0.75) 35%, rgba(0,0,0,0.25) 70%, transparent 100%)' }}
        />
      )}
      <div aria-hidden className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(to right, rgba(14,10,24,0.35), rgba(14,10,24,0.55) 45%, rgba(14,10,24,0.85))' }} />
      <div className="relative h-full flex items-center gap-[clamp(10px,1.4vw,28px)] px-[clamp(12px,1.6vw,32px)]">
        <GameIcon gameId={gameId} size={Math.round(H * 0.72)} />
        <div className="min-w-0 shrink-0 max-w-[30%]">
          <p className="font-display font-black leading-none truncate" style={{ fontSize: H * 0.36, color: 'var(--tv)', textShadow: '0 3px 0 rgba(0,0,0,0.55)' }} data-testid="tv-game-name">{g.name}</p>
          <p className="text-paper/80 font-bold uppercase tracking-wider truncate mt-1" style={{ fontSize: H * 0.14 }}>{g.genre}</p>
        </div>
        <div className="min-w-0 flex-1 flex flex-col justify-center gap-1 pl-[clamp(8px,1.2vw,24px)] border-l-2 border-white/15">
          <p className="font-display font-black leading-tight truncate" style={{ fontSize: H * 0.25 }} data-testid="tv-phase">
            <span style={{ color: 'var(--tv-2)' }}>{kicker}</span>
          </p>
          {caption && (
            <AnimatePresence mode="wait" initial={false}>
              <motion.p key={caption} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} className="font-bold text-paper/90 leading-snug line-clamp-2" style={{ fontSize: H * 0.17 }} data-testid="tv-caption">
                {caption}
              </motion.p>
            </AnimatePresence>
          )}
        </div>
        {endsAt != null && startedAt != null && <Countdown endsAt={endsAt} startedAt={startedAt} size={Math.round(H * 0.74)} quiet={quietTimer} />}
        <div className={`items-center gap-3 rounded-2xl bg-ink/85 ring-1 ring-white/15 p-1.5 pr-4 shrink-0 ${showJoin ? 'flex' : 'hidden'}`} data-testid="tv-join">
          <QR value={joinUrl} size={qr} />
          <div>
            <p className="text-mute font-bold leading-tight" style={{ fontSize: H * 0.12 }}>Join at {new URL(joinUrl).host}</p>
            <p className="font-display font-black tracking-[0.12em] leading-none tabular-nums" style={{ fontSize: H * (code.length <= 5 ? 0.36 : 0.3) }} data-testid="display-code">{code}</p>
          </div>
        </div>
        <TvMusicControls compact />
        <div className="flex flex-col gap-1.5 shrink-0">
          <button className="btn btn-sm btn-secondary" style={{ minHeight: 0, padding: 7 }} aria-label="Enable sound" onClick={() => { sound.unlock(); sound.play('select'); }}><Volume2 size={16} /></button>
          <FullscreenButton compact />
        </div>
      </div>
    </header>
  );
}

/** Lobby header: platform logo, what's up next, and the screen buttons. */
export function TvLobbyHeader({ gameId }: { gameId: GameId }) {
  const vh = useViewportHeight();
  const H = Math.round(clamp(84, vh * 0.09, 110));
  return (
    <header className="relative shrink-0 flex items-center gap-4 px-[clamp(12px,1.6vw,32px)]" style={{ height: H }}>
      <Logo size="sm" />
      <div className="flex-1" />
      <TvMusicControls />
      <button className="btn btn-sm btn-secondary" aria-label="Enable sound" onClick={() => { sound.unlock(); sound.play('select'); }}><Volume2 size={18} /></button>
      <FullscreenButton />
      <span className="sr-only" data-game-next={gameId} />
    </header>
  );
}

/** Kicker for the header: "Round 2 of 5 · Vote". */
export function phaseKicker(phase: { name: string; round: number; totalRounds: number }): string {
  const label = PHASE_LABEL[phase.name] ?? phase.name.replace(/_/g, ' ').toLowerCase().replace(/^\w/, (c) => c.toUpperCase());
  return phase.round > 0 && phase.totalRounds > 0 ? `Round ${phase.round} of ${phase.totalRounds} · ${label}` : label;
}

export { phaseCaption };

/** Rank each player held before the last scored round (from total minus that round's points). */
function previousRanks(scores: ScoreLine[]): Map<string, number> {
  const before = scores.map((s) => ({ id: s.playerId, t: s.total - s.lastRound }));
  const sorted = [...before].sort((a, b) => b.t - a.t);
  const out = new Map<string, number>();
  for (const b of before) out.set(b.id, sorted.findIndex((x) => x.t === b.t) + 1);
  return out;
}

/**
 * Live scoreboard sidebar: every player with avatar and score, the leader crowned, and what changed in the
 * last scored round (points gained, places moved). Rows size themselves so up to 20 players always fit.
 */
export function TvScoreboard({ scores, title = 'Scoreboard' }: { scores: ScoreLine[]; title?: string }) {
  const { member, reducedMotion } = usePartyUi();
  const box = useRef<HTMLOListElement>(null);
  const [{ w, h }, setSize] = useState({ w: 300, h: 600 });
  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    setSize({ w: el.clientWidth, h: el.clientHeight });
    const ro = new ResizeObserver(([e]) => e && setSize({ w: e.contentRect.width, h: e.contentRect.height }));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const rows = [...scores].sort((a, b) => a.rank - b.rank || b.total - a.total);
  const prev = previousRanks(scores);
  const n = Math.max(1, rows.length);
  const gap = n > 12 ? 4 : 8;
  const rowH = clamp(26, (h - gap * (n - 1)) / n, 84);
  // Bounded by the sidebar's width too, so name, change and total always fit side by side.
  const av = Math.round(Math.min(rowH * 0.72, w * 0.15));
  const font = clamp(13, Math.min(rowH * 0.34, w * 0.068), 28);
  const anyScore = rows.some((r) => r.total !== 0);
  return (
    <aside className="shrink-0 flex flex-col min-h-0 border-l-2 border-white/10 bg-ink/55 backdrop-blur-sm" style={{ width: 'clamp(250px, 21vw, 420px)' }} data-testid="tv-scoreboard" aria-label={title}>
      <div className="px-4 pt-3 pb-2 flex items-center gap-2">
        <Trophy size={22} style={{ color: 'var(--tv-2)' }} aria-hidden />
        <h2 className="font-display font-black text-[clamp(18px,2.2vh,28px)] leading-none flex-1">{title}</h2>
        <span className="text-mute font-bold text-[clamp(12px,1.5vh,18px)]">{rows.length} playing</span>
      </div>
      <ol ref={box} className="flex-1 min-h-0 flex flex-col px-3 pt-2 pb-3 overflow-hidden" style={{ gap }}>
        <AnimatePresence initial={false}>
          {rows.map((s) => {
            const m = member(s.playerId);
            const lead = anyScore && s.rank === 1;
            const was = prev.get(s.playerId) ?? s.rank;
            const moved = s.lastRound !== 0 ? was - s.rank : 0;
            return (
              <motion.li
                key={s.playerId}
                layout={!reducedMotion}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: m?.connected === false ? 0.55 : 1, x: 0 }}
                className="relative flex items-center gap-2 rounded-2xl px-2 shrink-0"
                style={{
                  height: rowH, fontSize: font,
                  background: lead ? 'linear-gradient(90deg, color-mix(in oklab, var(--tv) 45%, #1a1428), #1a1428)' : 'color-mix(in oklab, #1a1428 92%, transparent)',
                  // An outline, not a box-shadow: motion's layout animation takes over box-shadow and can leave
                  // a stale ring on a row that just lost the lead.
                  outline: lead ? '2px solid var(--tv)' : '2px solid transparent', outlineOffset: -2,
                }}
                data-testid="tv-score-row"
                data-leader={lead ? '1' : undefined}
              >
                <span className="font-display font-black text-center tabular-nums shrink-0" style={{ width: font * 1.3, color: lead ? 'var(--color-paper)' : 'var(--color-mute)' }}>{s.rank}</span>
                <span className="relative shrink-0">
                  {m ? <Avatar id={m.avatar} size={av} color={m.color} /> : <span style={{ width: av, height: av }} className="block rounded-full bg-ink-3" />}
                  {lead && <Crown size={Math.max(14, av * 0.42)} className="absolute -top-[30%] left-1/2 -translate-x-1/2 text-brand-2 drop-shadow" aria-label="leader" />}
                </span>
                <span className="font-bold truncate flex-1 min-w-0">
                  {m?.nickname ?? 'Former player'}
                  {m?.connected === false && <WifiOff size={font * 0.8} className="inline ml-1 text-mute" aria-label="reconnecting" />}
                </span>
                {moved > 0 && <span className="text-good font-black flex items-center shrink-0" style={{ fontSize: font * 0.7 }} aria-label={`up ${moved}`}><ArrowUp size={font * 0.75} />{moved}</span>}
                {moved < 0 && <span className="text-bad font-black flex items-center shrink-0" style={{ fontSize: font * 0.7 }} aria-label={`down ${-moved}`}><ArrowDown size={font * 0.75} />{-moved}</span>}
                {s.lastRound !== 0 && (
                  <span className={`font-black tabular-nums shrink-0 rounded-full px-1.5 ${s.lastRound > 0 ? 'text-good bg-good/15' : 'text-bad bg-bad/15'}`} style={{ fontSize: font * 0.7 }}>
                    {s.lastRound > 0 ? '+' : ''}{s.lastRound.toLocaleString()}
                  </span>
                )}
                <span className="font-display font-black tabular-nums shrink-0 text-right" style={{ minWidth: font * 2 }}>{s.total.toLocaleString()}</span>
              </motion.li>
            );
          })}
        </AnimatePresence>
      </ol>
    </aside>
  );
}

/**
 * Fits the game's own screen into the main area: when it would overflow (at 720p, say) it is scaled down
 * evenly instead of clipped, so nothing is hidden behind the frame. It never scales up. `id` (phase+round)
 * resets the fit when the screen changes.
 */
export function FitBox({ id, children }: { id: string; children: ReactNode }) {
  const outer = useRef<HTMLDivElement>(null);
  const inner = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState<{ w: number; h: number } | null>(null);
  const [s, setS] = useState(1);
  useLayoutEffect(() => {
    const el = outer.current;
    if (!el) return;
    setBox({ w: el.clientWidth, h: el.clientHeight });
    const ro = new ResizeObserver(([e]) => {
      if (!e) return;
      setBox({ w: e.contentRect.width, h: e.contentRect.height });
      setS(1);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  useEffect(() => setS(1), [id]);
  useEffect(() => {
    if (!box) return;
    const check = () => {
      const i = inner.current;
      if (!i) return;
      const needH = i.scrollHeight;
      const needW = i.scrollWidth;
      setS((cur) => {
        if (needH <= i.clientHeight + 2 && needW <= i.clientWidth + 2) return cur;
        return Math.max(0.45, Math.min(cur, box.h / needH, box.w / needW));
      });
    };
    const raf = requestAnimationFrame(check);
    const t = window.setInterval(check, 400);
    return () => {
      cancelAnimationFrame(raf);
      clearInterval(t);
    };
  }, [box, id]);
  return (
    <div ref={outer} className="relative flex-1 min-w-0 min-h-0 overflow-hidden" data-testid="tv-main">
      {box && (
        <div
          ref={inner}
          className="absolute top-0 flex flex-col overflow-hidden"
          // Height grows by 1/s but width only by 1/sqrt(s): content whose height follows its width (a board
          // with an aspect ratio) then still converges to a fit instead of growing with the box. The narrower
          // result is centred.
          style={{ left: (box.w - box.w * Math.sqrt(s)) / 2, width: box.w / Math.sqrt(s), height: box.h / s, transform: s === 1 ? undefined : `scale(${s})`, transformOrigin: '0 0' }}
          data-fit={s.toFixed(2)}
        >
          {children}
        </div>
      )}
    </div>
  );
}
